import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader';
import * as THREE from 'three';
import { customMaterial } from 'lib/mapper';
import React, { useEffect, useState, useMemo } from 'react';
import { default_texture } from 'components/base/images';

interface MeshPLYProps {
  url?: string;
  fileData?: ArrayBuffer | File | Blob | Uint8Array;
  textureUrl?: string;
  textureFileData?: ArrayBuffer;
  extension: string;
  isModelFileLoadingProgress: {
    value: boolean;
    onChange: (e: boolean) => void;
    setValue: React.Dispatch<boolean>;
  };
  onModelFile: (mesh: THREE.Mesh) => void;
}

const MeshPLY: React.FC<MeshPLYProps> = ({
  url,
  fileData,
  textureUrl,
  textureFileData,
  extension,
  isModelFileLoadingProgress,
  onModelFile,
}): any => {
  const [geometry, setGeometry] = useState<THREE.BufferGeometry | null>(null);
  const [texture, setTexture] = useState<THREE.Texture | null>(null);

  useEffect(() => {
    const loadGeometry = async () => {
      try {
        const loader = new PLYLoader();
        let loadedGeometry: THREE.BufferGeometry;

        if (fileData) {
          let arrayBuffer: ArrayBuffer;
          
          // fileData 타입에 따라 ArrayBuffer로 변환
          if (fileData instanceof ArrayBuffer) {
            arrayBuffer = fileData;
          } else if (fileData instanceof File || fileData instanceof Blob) {
            arrayBuffer = await fileData.arrayBuffer();
          } else if (fileData instanceof Uint8Array) {
            arrayBuffer = fileData.buffer.slice(fileData.byteOffset, fileData.byteOffset + fileData.byteLength);
          } else {
            console.error('fileData is not a valid type:', typeof fileData, fileData);
            return;
          }
          
          loadedGeometry = loader.parse(arrayBuffer);
        } else if (url) {
          loadedGeometry = await new Promise((resolve, reject) => {
            loader.load(url, resolve, undefined, reject);
          });
        } else {
          return;
        }

        if (!loadedGeometry?.attributes?.normal) {
          loadedGeometry.computeVertexNormals();
        }
        setGeometry(loadedGeometry);
      } catch (error) {
        console.error('Error loading PLY geometry:', error);
      }
    };

    loadGeometry();
  }, [url, fileData]);

  useEffect(() => {
    const loadTexture = async () => {
      try {
        const loader = new THREE.TextureLoader();
        let loadedTexture: THREE.Texture;

        if (textureFileData) {
          const blob = new Blob([textureFileData]);
          const textureDataUrl = URL.createObjectURL(blob);
          loadedTexture = await new Promise((resolve, reject) => {
            loader.load(textureDataUrl, (tex) => {
              URL.revokeObjectURL(textureDataUrl);
              resolve(tex);
            }, undefined, reject);
          });
        } else if (textureUrl) {
          loadedTexture = await new Promise((resolve, reject) => {
            loader.load(textureUrl, resolve, undefined, reject);
          });
        } else {
          loadedTexture = await new Promise((resolve, reject) => {
            loader.load(default_texture, resolve, undefined, reject);
          });
        }

        setTexture(loadedTexture);
      } catch (error) {
        console.error('Error loading texture:', error);
        // Fallback to default texture
        const loader = new THREE.TextureLoader();
        loader.load(default_texture, setTexture);
      }
    };

    loadTexture();
  }, [textureUrl, textureFileData]);

  const material = useMemo(() => {
    if (!texture) return null;
    
    return new THREE.ShaderMaterial({
      ...customMaterial,
      uniforms: {
        ...THREE.UniformsUtils.clone(customMaterial.uniforms),
        textureMap: { value: texture },
        isTexture: { value: false },
        isVertexColor: { value: true },
        extension: { value: extension },
      },
    });
  }, [texture, extension]);

  return (
    !!material &&
    !!geometry && (
      <mesh
        ref={onModelFile}
        castShadow
        receiveShadow
        geometry={geometry}
        material={material}
        renderOrder={-1}
      />
    )
  );
};

export default MeshPLY;
